import type { InferGetStaticPropsType } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { type ReactElement, useEffect } from 'react';

import AppSidebarLayout from '@/components/AppSidebarLayout';
import Button from '@/components/Button';
import Icon from '@/components/Icon';
import OfferDetailView from '@/components/JobBoard/OfferDetail/OfferDetailView';
import PremiumUpsellBottomBanner from '@/components/JobBoard/StickyBottomBanner';
import Text from '@/components/Text';
import applicantLocationCountries from '@/data/applicant_location_countries.json';
import {
  getExternalOfferDetail,
  getExternalOffers,
  getSimilarExternalOffer,
} from '@/services/external-offers';
import { capitalizeFirstLetter } from '@/utils/cvGenerator';
import { createMetaTags } from '@/utils/seo';

const ExternalOfferDetail = ({
  id,
  offerData,
  similarOffers,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const {
    companyImage,
    companyName,
    isHybrid,
    isRemote,
    jobDescriptionTextWithStyling,
    jobTitle,
    locations,
    seniorityLevel,
    salaryPeriod,
    salaryLower,
    salaryUpper,
    createdAt,
    companyDescription,
    salaryCurrency,
    isDeprecated,
    skills,
    requirements,
    benefits,
  } = offerData;

  useEffect(() => {
    document.getElementById('main-scroll-container')?.scrollTo({ top: 0 });
  }, [id]);

  const stripHtml = (html: string) =>
    html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const level = capitalizeFirstLetter(seniorityLevel);
  const city = locations[0] ?? null;
  const metaTitle = `${[level, jobTitle, city]
    .filter(Boolean)
    .join(' • ')} | MindPal Jobs`;

  const getWorkType = () => {
    if (isRemote) return 'Remote';
    if (isHybrid) return 'Hybrid';
    return 'On-site';
  };
  const workType = getWorkType();
  const salaryPart =
    salaryLower && salaryUpper
      ? ` Salary: ${salaryLower}–${salaryUpper} ${salaryCurrency}.`
      : '';
  const metaDescription = `${level} ${jobTitle} at ${companyName}${
    city ? ` in ${city}` : ''
  }. ${workType}.${salaryPart} Apply now on MindPal Jobs.`;

  const jobPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: jobTitle,
    datePosted: new Date(createdAt).toISOString(),
    hiringOrganization: {
      '@type': 'Organization',
      name: companyName,
      ...(companyImage?.url && { logo: companyImage.url }),
      ...(companyDescription && { description: companyDescription }),
    },
    ...(locations.length > 0 && {
      jobLocation: locations.map((loc) => ({
        '@type': 'Place',
        address: { '@type': 'PostalAddress', addressLocality: loc },
      })),
    }),
    ...(isRemote && { jobLocationType: 'TELECOMMUTE' }),
    ...(isRemote && {
      applicantLocationRequirements: applicantLocationCountries.map(
        (country) => ({
          '@type': 'Country',
          name: country,
        })
      ),
    }),
    ...(salaryLower && {
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: salaryCurrency,
        value: {
          '@type': 'QuantitativeValue',
          minValue: salaryLower,
          maxValue: salaryUpper,
          unitText: salaryPeriod?.toUpperCase() ?? 'MONTH',
        },
      },
    }),
    description: stripHtml(jobDescriptionTextWithStyling),
    directApply: false,
    employmentType: seniorityLevel === 'INTERN' ? 'INTERN' : 'FULL_TIME',
    validThrough: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    ...(skills.length > 0 && { skills: skills.join(', ') }),
    ...(requirements.length > 0 && { qualifications: requirements.join(', ') }),
    ...(benefits && { jobBenefits: benefits }),
  };

  return (
    <>
      <style jsx global>
        {`
          body {
            background: #f5f5f5;
          }
        `}
      </style>
      <Head>
        {createMetaTags({
          title: metaTitle,
          description: metaDescription,
          ...(companyImage?.url && { ogImage: companyImage.url }),
        })}
        <link
          rel="canonical"
          href={`https://mindpal.co/jobs/${id}`}
          key="canonical"
        />
        {!isDeprecated && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(jobPostingSchema),
            }}
          />
        )}
      </Head>
      <div className="flex items-center justify-center bg-neutral-0 p-4 text-neutral-1000 md:relative md:px-6">
        <div className="w-full max-w-screen-4xl">
          <Link href="/jobs">
            <Button
              variant="unstyled"
              size="xs"
              tabIndex={-1}
              className="flex items-center gap-1"
            >
              <Icon
                icon="material-symbols-light:arrow-left-alt"
                className="text-xl"
              />
              <Text variant="body-caption">Back to all jobs</Text>
            </Button>
          </Link>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4 text-neutral-1000 md:p-6">
        <div className="flex w-full max-w-screen-4xl self-center">
          <OfferDetailView
            offer={offerData}
            similarOffers={similarOffers?.results ?? []}
          />
        </div>
      </div>
      <PremiumUpsellBottomBanner />
    </>
  );
};

export default ExternalOfferDetail;

export const getStaticPaths = async () => {
  const maxStaticPages = 100;
  const pageSize = 20;
  const firstPage = await getExternalOffers({ offset: 0, limit: pageSize });
  const { count } = firstPage;

  const allPages = await Promise.all(
    Array.from({ length: count / pageSize })
      .slice(0, maxStaticPages / pageSize)
      .map(async (_d, page) => {
        const { results } = await getExternalOffers({
          offset: page * pageSize,
          limit: pageSize,
        });
        return results.map(({ id }) => id);
      })
  );
  const allResults = allPages.flat();

  return {
    paths: allResults.map((id) => ({
      params: { id: id.toString() },
    })),
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({
  params,
}: {
  params: { id: number | string };
}) => {
  try {
    const offerData = await getExternalOfferDetail(params?.id as string);
    if (!offerData) return { notFound: true };

    // Similar offers feed the sidebar list, the similar-roles carousel and the similar jobs grid.
    const similarOffers = await getSimilarExternalOffer({
      id: params?.id as string,
      limit: 20,
    });

    return {
      props: { id: params.id, offerData, similarOffers },
      revalidate: 30,
    };
  } catch {
    return { notFound: true };
  }
};

ExternalOfferDetail.getLayout = (page: ReactElement) => (
  <AppSidebarLayout>{page}</AppSidebarLayout>
);
